import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import {
  openCaptchaModal,
  setCaptchaToken,
} from 'src/features/home/ducks/home.reducer'
import { CAPTCHA_REQUIRED, INVALID_CAPTCHA } from 'src/utils/errorCode'
import UrlPattern from 'url-pattern'

import store from '../redux'

import * as endpoints from './endpoints'
import { type ApiResponse } from './types'

interface IAxiosConfig extends AxiosRequestConfig { }

function generateEndpoint(url: keyof typeof endpoints, params?: object) {
  const pattern = new UrlPattern(endpoints[url])

  return pattern.stringify(params)
}

// Resolves with the new captchaToken once the user solves the captcha;
// rejects if the captcha modal closes without a token (user cancelled).
async function waitForCaptchaToken(): Promise<string> {
  return await new Promise((resolve, reject) => {
    const currentToken = store.getState()?.home?.captchaToken
    if (currentToken) {
      resolve(currentToken)
      return
    }

    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      const token = state?.home?.captchaToken
      const modalOpen = state?.home?.captchaModalOpen

      if (token) {
        unsubscribe()
        resolve(token)
      } else if (!modalOpen) {
        unsubscribe()
        reject(new Error('Captcha cancelled'))
      }
    })
  })
}

function isCaptchaChallengeResponse(responseCode: unknown): boolean {
  return responseCode === CAPTCHA_REQUIRED || responseCode === INVALID_CAPTCHA
}

async function makeRequest(
  payload: any,
  config?: IAxiosConfig,
): Promise<ApiResponse> {
  try {
    const baseURL = payload.baseUrl || process.env.REACT_APP_API_BASE_URL
    const api = axios.create({
      baseURL,
    })

    const { data, query, path, method } = payload

    const url = generateEndpoint(path.url, path.params)

    const axiosConfig: any = config || {
      headers: {
        'Content-Type': 'application/json',
      },
    }

    const captchaToken = store.getState()?.home?.captchaToken

    const requestObject: AxiosRequestConfig = {
      headers: {
        ...axiosConfig.headers,
      },
      url,
      method,
    }

    if (method === 'GET' && query) {
      requestObject.params = query
    }

    if (method !== 'GET' && data) {
      // /card and /fraudulent need captchaToken. Read it fresh from the
      // store so the retry after a captcha-solve carries the new token,
      // not a stale one. /fraudulent sends multipart FormData, so append
      // (deleting first to stay idempotent across the retry); JSON bodies
      // get the token spread in.
      const needsCaptcha =
        path?.url === 'GET_CARD' || path?.url === 'FRAUDULENT'
      if (needsCaptcha && captchaToken) {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          data.delete('captchaToken')
          data.append('captchaToken', captchaToken)
          requestObject.data = data
        } else {
          requestObject.data = { ...data, captchaToken }
        }
      } else {
        requestObject.data = data
      }
    }

    const response: AxiosResponse<any, any> = await api(requestObject)

    const responseSuccess = {
      response,
      error: null,
    }
    return responseSuccess
  } catch (error: any) {
    const { message = 'Internal server error' } = error || {}
    const responseError = {
      response: null,
      error: {
        message,
        status: 500,
      },
    }

    // If error is typeof axios error
    if (error instanceof AxiosError) {
      const { response } = error satisfies AxiosError
      const { data } = response || {}

      // If the backend returned our standard JSON envelope (even on a
      // non-2xx status like 403 for CAPTCHA_REQUIRED), surface it as a
      // normal response so downstream callers can branch on responseCode
      // / errorCode. This is what lets the universal captcha-retry below
      // kick in on HTTP 403 + responseCode=CAPTCHA_REQUIRED.
      if (
        response &&
        data &&
        typeof data === 'object' &&
        'responseCode' in data
      ) {
        return { response, error: null }
      }

      const { message: axiosResponseError } = data || {}
      responseError.error.message = axiosResponseError || message
      responseError.error.status = error.response?.status || 500

      return responseError
    }

    return responseError
  }
}

const CAPTCHA_CANCELLED_RESPONSE: ApiResponse = {
  response: null,
  error: {
    message: 'CAPTCHA verification cancelled.',
    status: 401,
  },
}

function endpointRequiresCaptcha(payload: any): boolean {
  return payload?.path?.url === 'GET_CARD' || payload?.path?.url === 'FRAUDULENT'
}

// Universal captcha handling:
//  - Pre-flight: if the endpoint requires a captcha and none is in
//    Redux yet, open the modal and WAIT for the user to solve it
//    BEFORE the request goes out. No wasted round-trip.
//  - Post-flight: if the backend returns CAPTCHA_REQUIRED (or
//    INVALID-CAPTCHA-400) anyway (e.g. token expired mid-flight),
//    clear the stale token, open the modal, wait, retry once.
//  - User cancels the modal either time → surface a cancellation
//    error to the caller instead of an infinite wait.
async function http(
  payload: any,
  config?: IAxiosConfig,
): Promise<ApiResponse> {
  if (endpointRequiresCaptcha(payload)) {
    const currentToken = store.getState()?.home?.captchaToken
    if (!currentToken) {
      store.dispatch(openCaptchaModal())
      try {
        await waitForCaptchaToken()
      } catch {
        return CAPTCHA_CANCELLED_RESPONSE
      }
    }
  }

  let result = await makeRequest(payload, config)

  const firstResponseCode = (result.response?.data)?.responseCode
  if (isCaptchaChallengeResponse(firstResponseCode)) {
    store.dispatch(setCaptchaToken(null))
    store.dispatch(openCaptchaModal())

    try {
      await waitForCaptchaToken()
    } catch {
      return CAPTCHA_CANCELLED_RESPONSE
    }

    // Retry once with the newly-issued token. If the backend still says
    // captcha is required, surface that as the final result — no infinite
    // loop.
    result = await makeRequest(payload, config)
  }

  return result
}

export default http
